import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { getStorage, setStorage } from "../storage/storageHelper";

export type Theme = "dark" | "light" | "auto";
export type AppliedTheme = "dark" | "light" | undefined;

interface DarkModeContextProps {
  theme: Theme;
  appliedTheme: AppliedTheme;
  toggleTheme: (newTheme: Theme) => void;
}

const DarkModeContext = createContext<DarkModeContextProps | undefined>(
  undefined
);

export const useDarkMode = (): DarkModeContextProps => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error("useDarkMode must be used within a DarkModeProvider");
  }
  return context;
};

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider = ({ children }: DarkModeProviderProps) => {
  const [theme, setTheme] = useState<Theme>();
  const [appliedTheme, setAppliedTheme] = useState<AppliedTheme>();

  const toggleTheme = useCallback(async (newTheme: Theme) => {
    await setStorage({ key: "PREFERENCES", data: { theme: newTheme } });
    setTheme(newTheme);
  }, []);

  useEffect(() => {
    (async () => {
      const pref = await getStorage("PREFERENCES");

      setTheme(pref?.theme ?? "auto");
    })();
  }, []);

  useEffect(() => {
    if (
      theme === "dark" ||
      (theme === "auto" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      document.documentElement.classList.add("dark");

      setAppliedTheme("dark");
    } else {
      document.documentElement.classList.remove("dark");

      setAppliedTheme("light");
    }
  }, [theme]);

  if (!theme) {
    return null;
  }

  return (
    <DarkModeContext.Provider value={{ theme, appliedTheme, toggleTheme }}>
      {children}
    </DarkModeContext.Provider>
  );
};
